import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { Block } from '@mui/icons-material';
import { useModulePermissions } from '../hooks/useModulePermissions';
import SchoolAdminDashboard from './dashboard/SchoolAdminDashboard';
import TeacherDashboard from './dashboard/TeacherDashboard';
import EmployeeDashboard from './dashboard/EmployeeDashboard';

/**
 * Dashboard Page
 * Routes to appropriate dashboard based on user role
 * - System Admin: Super Admin Dashboard (placeholder)
 * - School Admin: School Admin Dashboard
 * - Teacher: Teacher Dashboard
 * - Others: Generic placeholder (future: role-specific dashboards)
 */
const DashboardPage: React.FC = () => {
  const { canRead, isSchoolAdmin, isSystemAdmin, isTeacher } = useModulePermissions();

  // Permission check - simple elegant access denied
  if (!canRead('dashboard')) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="70vh"
      >
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            maxWidth: 420,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
          }}
        >
          <Block 
            sx={{ 
              fontSize: 56, 
              color: '#9e9e9e',
              mb: 2 
            }} 
          />
          <Typography variant="h6" fontWeight="500" gutterBottom color="text.primary">
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You don't have permission to view Dashboard.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Contact your administrator for access.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Route to appropriate dashboard based on role
  // System Admin - future: Super Admin Dashboard
  if (isSystemAdmin) {
    // TODO: Implement Super Admin Dashboard
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Super Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Coming soon - Super Admin Dashboard with platform-wide metrics
        </Typography>
      </Box>
    );
  }

  // School Admin Dashboard
  if (isSchoolAdmin) {
    return <SchoolAdminDashboard />;
  }

  // Teacher Dashboard
  if (isTeacher) {
    return <TeacherDashboard />;
  }

  // Every other staff role (Receptionist, Accountant, etc.) — reuses the
  // attendance + leave cards from the Teacher dashboard. No new card was
  // built for this view; the same hook fetches the same data.
  return <EmployeeDashboard />;
};

export default DashboardPage;